import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Save, CheckCheck, Lock, BellRing } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUSES = ["Present", "Absent", "Half Day", "Leave", "LWP", "Holiday"];
const today = () => new Date().toISOString().slice(0, 10);
const tone = (v) => v === "Present" ? "text-emerald-700" : v === "Absent" || v === "LWP" ? "text-red-700"
  : v === "Half Day" ? "text-amber-700" : v === "Leave" ? "text-blue-700" : "text-slate-600";

export default function EmployeeAttendance() {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [locked, setLocked] = useState(false);
  const [monthStatus, setMonthStatus] = useState("Draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [reminding, setReminding] = useState(false);

  const remind = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/attendance/reminder/run");
      toast.success(data.message);
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setReminding(false);
  };

  const load = async (d) => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get(`/attendance/day?date=${d}`), api.get("/attendance-corrections")]);
      setRows(r.data.rows); setLocked(r.data.locked); setMonthStatus(r.data.month_status);
      setPending(c.data.filter((x) => x.status === "Pending"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const setRow = (id, key, value) => setRows((rs) => rs.map((r) => (r.employee_id === id ? { ...r, [key]: value } : r)));
  const allPresent = () => setRows((rs) => rs.map((r) => ({ ...r, status: r.status || "Present" })));

  const save = async () => {
    setSaving(true);
    try {
      const entries = rows.filter((r) => r.status).map((r) => ({ employee_id: r.employee_id, employee_name: r.employee_name, status: r.status, remarks: r.remarks || "" }));
      const { data } = await api.post("/attendance/bulk", { attendance_date: date, entries });
      toast.success(data.message); load(date);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const review = async (id, action) => {
    try { await api.post(`/attendance-corrections/${id}/${action}`); toast.success(`Correction ${action}d`); load(date); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: rows.filter((r) => r.status === s).length }), {});

  return (
    <div data-testid="employee-attendance">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Daily Attendance</h1>
            <p className="text-sm text-slate-500 mt-0.5">Mark every employee for one day · {rows.length} employee{rows.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {locked && <Badge data-testid="attendance-locked-badge" variant="outline" className="rounded-sm bg-red-50 text-red-700 border-red-200 gap-1"><Lock className="w-3 h-3" /> {monthStatus} — locked</Badge>}
          <Input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 bg-white" />
          <Button data-testid="attendance-all-present" variant="outline" disabled={locked} onClick={allPresent} className="rounded-sm gap-1.5"><CheckCheck className="w-4 h-4" /> <span className="hidden sm:inline">Fill Present</span></Button>
          <Button data-testid="attendance-remind-btn" variant="outline" onClick={remind} disabled={reminding} className="rounded-sm gap-1.5"><BellRing className="w-4 h-4" /> <span className="hidden sm:inline">{reminding ? "Checking..." : "Remind Pending"}</span></Button>
          <Button data-testid="attendance-save" onClick={save} disabled={saving || locked || !rows.length} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}</Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUSES.map((s) => (
          <div key={s} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm" data-testid={`att-count-${s.toLowerCase().replace(" ", "-")}`}>
            <span className={`font-bold tabular-nums ${tone(s)}`}>{counts[s]}</span> <span className="text-slate-500">{s}</span>
          </div>
        ))}
      </div>

      {pending.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg mb-5 overflow-hidden" data-testid="correction-requests">
          <div className="px-4 py-2.5 bg-amber-50 text-sm font-semibold text-amber-800 border-b border-amber-200">{pending.length} attendance correction request(s) pending</div>
          <table className="w-full text-sm">
            <tbody>
              {pending.map((c) => (
                <tr key={c.id} data-testid={`correction-${c.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{c.employee_name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{c.attendance_date}</td>
                  <td className="px-4 py-2.5 text-slate-600">{c.current_status} → <span className="font-semibold text-slate-900">{c.requested_status}</span></td>
                  <td className="px-4 py-2.5 text-slate-500">{c.reason}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button data-testid={`correction-approve-${c.id}`} size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white mr-2" onClick={() => review(c.id, "approve")}>Approve</Button>
                    <Button data-testid={`correction-reject-${c.id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => review(c.id, "reject")}>Reject</Button>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Employee", "Code", "Department", "Status", "Remarks"].map((h) => (
              <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-400">Loading employees...</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-400" data-testid="attendance-empty">No active employees. Add employees first.</td></tr>
              : rows.map((r) => (
                <tr key={r.employee_id} data-testid={`att-row-${r.employee_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.employee_name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.employee_code || "-"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.department || "-"}</td>
                  <td className="px-4 py-2.5 w-48">
                    <Select value={r.status || undefined} disabled={locked} onValueChange={(v) => setRow(r.employee_id, "status", v)}>
                      <SelectTrigger data-testid={`att-status-${r.employee_id}`} className="bg-white h-9"><SelectValue placeholder="Not marked" /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2.5 w-56">
                    <Input data-testid={`att-remarks-${r.employee_id}`} disabled={locked} value={r.remarks} onChange={(e) => setRow(r.employee_id, "remarks", e.target.value)} className="bg-white h-9" placeholder="optional" />
                  </td>
                </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
